import { api } from "@/lib/api-client";
import type { Paginated } from "../agents/api";
import type { ApiKey, ApiKeysEndpoints, NewApiKey } from "@/components/ApiKeysPanel";

// ── Team Management ─────────────────────────────────────────────────────────
export type TeamMember = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name?: string;
  role: "admin" | "manager" | "member" | "owner" | string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
};

export const teamApi = {
  list: async () => {
    const res = await api.get<Paginated<TeamMember> | TeamMember[]>(
      "/auth/users/",
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  create: async (data: {
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    password?: string;
  }) => {
    const res = await api.post<TeamMember>("/auth/users/", data);
    return res.data;
  },
  update: async (
    id: string,
    data: { first_name?: string; last_name?: string; role?: string },
  ) => {
    const res = await api.patch<TeamMember>(`/auth/users/${id}/`, data);
    return res.data;
  },
  deactivate: async (id: string) => {
    await api.post(`/auth/users/${id}/deactivate/`);
  },
  reactivate: async (id: string) => {
    await api.post(`/auth/users/${id}/reactivate/`);
  },
};

// ── API Providers (encrypted creds) ─────────────────────────────────────────
export type ProviderCredential = {
  id: string;
  provider: string;
  label: string;
  api_key_prefix: string;
  api_endpoint: string;
  is_active: boolean;
  last_tested_at: string | null;
  last_error: string;
  created_at: string;
};

export const providersApi = {
  list: async () => {
    const res = await api.get<Paginated<ProviderCredential> | ProviderCredential[]>(
      "/tenants/providers/",
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  create: async (data: {
    provider: string;
    label?: string;
    api_key: string;
    api_endpoint?: string;
  }) => {
    const res = await api.post<ProviderCredential>("/tenants/providers/", data);
    return res.data;
  },
  update: async (
    id: string,
    data: { label?: string; api_endpoint?: string; api_key?: string },
  ) => {
    const res = await api.patch<ProviderCredential>(
      `/tenants/providers/${id}/`,
      data,
    );
    return res.data;
  },
  remove: async (id: string) => {
    await api.delete(`/tenants/providers/${id}/`);
  },
  test: async (id: string) => {
    const res = await api.post<{ success: boolean; detail?: string }>(
      `/tenants/providers/${id}/test/`,
    );
    return res.data;
  },
};

// ── Branding (white-label) ──────────────────────────────────────────────────
export type Branding = {
  name: string;
  product_name: string;
  tagline: string;
  support_email: string;
  primary_color: string;
  logo: string | null;
  logo_url: string;
  logo_resolved: string;
  favicon: string | null;
  favicon_resolved: string;
};

export type PublicBranding = {
  slug: string;
  name: string;
  product_name: string;
  tagline: string;
  primary_color: string;
  logo_resolved: string;
  favicon_resolved: string;
} | null;

export const brandingApi = {
  get: async () => {
    const res = await api.get<Branding>("/tenants/branding/");
    return res.data;
  },
  update: async (data: Partial<{
    name: string;
    product_name: string;
    tagline: string;
    support_email: string;
    primary_color: string;
    logo_url: string;
  }>) => {
    const res = await api.patch<Branding>("/tenants/branding/", data);
    return res.data;
  },
  uploadFile: async (field: "logo" | "favicon", file: File) => {
    const form = new FormData();
    form.append(field, file);
    // Do NOT set Content-Type manually — axios needs to generate the
    // multipart boundary itself. Passing the default JSON header here
    // would break the boundary and DRF would fail to parse the form.
    const res = await api.patch<Branding>("/tenants/branding/", form, {
      headers: { "Content-Type": undefined },
    });
    return res.data;
  },
  clearFile: async (target: "logo" | "favicon" | "both") => {
    const res = await api.delete<Branding>(
      `/tenants/branding/?target=${target}`,
    );
    return res.data;
  },
  byHost: async (params: { host?: string; slug?: string }) => {
    const res = await api.get<PublicBranding>("/tenants/branding/by-host/", {
      params,
    });
    return res.data;
  },
};

// ── Audit Log ───────────────────────────────────────────────────────────────
export type AuditLog = {
  id: string;
  action: string;
  actor_email: string | null;
  target_type: string;
  target_id?: string | null;
  status_code?: number | null;
  ip_address?: string | null;
  created_at: string;
};

export const auditApi = {
  list: async (params?: { action?: string }) => {
    const res = await api.get<Paginated<AuditLog> | AuditLog[]>(
      "/audit/logs/",
      { params },
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  actionTypes: async () => {
    const res = await api.get<string[]>("/audit/logs/action_types/");
    return res.data;
  },
};

// ── API keys (Settings → API Keys) ──────────────────────────────────────────
export const apiKeysApi: ApiKeysEndpoints = {
  queryKey: ["api-keys"] as const,
  list: async () => {
    const res = await api.get<Paginated<ApiKey> | ApiKey[]>("/auth/api-keys/", {
      params: { page_size: 100 },
    });
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  create: async (data: NewApiKey) =>
    (await api.post<ApiKey & { key: string }>("/auth/api-keys/", data)).data,
  revoke: async (id: string) => {
    await api.post(`/auth/api-keys/${id}/revoke/`);
  },
};
